import { useState, useCallback, useEffect, useRef } from 'react';
import { FormData, FormStep } from '../types';
import {
  businessSchema,
  locationSchema,
  contactSchema,
  websiteSchema,
  designSchema,
} from '../schemas/businessInfo';
import { ZodSchema, ZodError } from 'zod';

const STORAGE_KEY = 'webgen-form-state';
const SAVE_DEBOUNCE_MS = 500;

/**
 * Wizard step definition. Each step of the 4-step wizard renders one or
 * more data sections. Sections still map 1:1 to the Zod schemas in
 * shared/ and the section-level form components — they just get grouped
 * visually.
 *
 * Section order within a step matters: that's the order the fields render
 * in the UI, and the order validation runs.
 */
export interface WizardStepDef {
  /** Stable ID for the step (used as React key). */
  id: string;
  /** Human-readable label shown in the stepper. */
  label: string;
  /** Data sections this step covers. Empty array = summary/review step. */
  sections: FormStep[];
  /** The section used as `currentStepKey` for backwards-compat callers
   *  (e.g. ReviewForm's edit buttons). Must be in `sections` unless the
   *  step has none (review), in which case use the step's own sentinel. */
  primarySection: FormStep;
}

export const WIZARD_STEPS: WizardStepDef[] = [
  {
    id: 'business-location',
    label: 'Business & Location',
    sections: ['business', 'location'],
    primarySection: 'business',
  },
  {
    id: 'website-contact',
    label: 'Website & Contact',
    sections: ['website', 'contact'],
    primarySection: 'website',
  },
  {
    id: 'design',
    label: 'Design',
    sections: ['design'],
    primarySection: 'design',
  },
  {
    id: 'review',
    label: 'Review & Generate',
    sections: [],
    primarySection: 'review',
  },
];

function loadSavedFormData(): FormData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.business &&
      parsed.location &&
      parsed.contact &&
      parsed.website &&
      parsed.design
    ) {
      return parsed as FormData;
    }
    return null;
  } catch {
    return null;
  }
}

const initialFormData: FormData = {
  business: {
    name: '',
    legalEntity: '',
    registrationNumber: '',
    vatNumber: '',
    businessType: '',
    industry: '',
  },
  location: {
    country: '',
    city: '',
    street: '',
    postalCode: '',
    region: '',
  },
  contact: {
    primaryEmail: '',
    supportEmail: '',
    legalEmail: '',
    privacyEmail: '',
    phone: '',
    businessHours: '',
  },
  website: {
    domain: '',
    language: '',
    languageCode: '',
    currency: 'EUR',
    targetRegion: 'EU',
  },
  design: {
    primaryColor: '#2563eb',
    secondaryColor: '#64748b',
    accentColor: '#f59e0b',
    fontFamily: 'Inter',
    borderRadius: 'sm',
    stylePreset: 'modern',
  },
};

const sectionSchemas: Record<FormStep, ZodSchema | null> = {
  business: businessSchema,
  location: locationSchema,
  contact: contactSchema,
  website: websiteSchema,
  design: designSchema,
  review: null,
};

export function useForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(
    () => loadSavedFormData() ?? initialFormData
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced save to localStorage whenever formData changes
  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      } catch {
        // Silently ignore storage errors (e.g., quota exceeded)
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [formData]);

  const currentWizardStep = WIZARD_STEPS[currentStep];
  const currentStepKey = currentWizardStep.primarySection;

  /**
   * Validate a single data section against its Zod schema.
   * On failure, populates `errors` and scrolls to the first offending field.
   */
  const validateStep = useCallback((step: FormStep, data: unknown): boolean => {
    const schema = sectionSchemas[step];
    if (!schema) return true;

    try {
      schema.parse(data);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          const path = err.path.join('.');
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
        requestAnimationFrame(() => {
          const firstError = document.querySelector('[aria-invalid="true"]');
          firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (firstError instanceof HTMLElement) firstError.focus();
        });
      }
      return false;
    }
  }, []);

  /**
   * Validate every section in the current wizard step. Stops at the first
   * failure so the user sees the earliest offending field in context.
   *
   * Only data-owning sections appear in a step's `sections` list (never
   * 'review', which has an empty sections array), so the indexed access
   * below is always into `FormData`'s real keys.
   */
  const validateCurrentWizardStep = useCallback((): boolean => {
    const step = WIZARD_STEPS[currentStep];
    for (const section of step.sections) {
      const sectionKey = section as Exclude<FormStep, 'review'>;
      if (!validateStep(sectionKey, formData[sectionKey])) {
        return false;
      }
    }
    return true;
  }, [currentStep, formData, validateStep]);

  const updateFormData = useCallback(
    (step: FormStep, data: Partial<FormData[keyof FormData]>) => {
      setFormData((prev) => ({
        ...prev,
        [step]: {
          ...prev[step as keyof FormData],
          ...data,
        },
      }));
      setErrors({});
    },
    []
  );

  const nextStep = useCallback(() => {
    if (validateCurrentWizardStep()) {
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
      return true;
    }
    return false;
  }, [currentStep, validateCurrentWizardStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      setErrors({});
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < WIZARD_STEPS.length) {
      setCurrentStep(step);
      setErrors({});
    }
  }, []);

  /**
   * Jump to whichever wizard step contains a given section. Used by the
   * Review screen's per-card "Edit" buttons — those still reference
   * section names ('business', 'design', etc.), so we translate to the
   * wizard step that hosts that section.
   */
  const goToSection = useCallback((section: FormStep) => {
    const idx = WIZARD_STEPS.findIndex((s) => s.sections.includes(section) || s.primarySection === section);
    if (idx >= 0) {
      setCurrentStep(idx);
      setErrors({});
    }
  }, []);

  const clearSavedForm = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silently ignore storage errors
    }
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setErrors({});
    clearSavedForm();
  }, [clearSavedForm]);

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return {
    formData,
    currentStep,
    currentStepKey,
    currentWizardStep,
    wizardSteps: WIZARD_STEPS,
    errors,
    isLastStep,
    isFirstStep,
    updateFormData,
    nextStep,
    prevStep,
    goToStep,
    goToSection,
    resetForm,
    validateStep,
    clearSavedForm,
  };
}

export default useForm;
